// Alliance screen (GDD 08 screen 6, slice subset — first-playable-slice S-5; spec §3.6): without an
// alliance, invitations, founding and the alliance list; as a member, tabs Overview / Members /
// Attacks (G-70) / Recruiting / Settings in `?tab=`. No chat, diplomacy or wars.
import type { MemberAlliance } from '@stars-and-ships/api-types';
import { useOutletContext, useSearchParams } from 'react-router';
import { useAlliance, useAllianceAttacks, useAllianceInvites } from '../api/queries';
import { AttacksTab } from '../components/alliance/AttacksTab';
import { Emblem } from '../components/alliance/Emblem';
import { MembersTab } from '../components/alliance/MembersTab';
import { NoAllianceView } from '../components/alliance/NoAllianceView';
import { OverviewTab } from '../components/alliance/OverviewTab';
import { RecruitingTab } from '../components/alliance/RecruitingTab';
import { SettingsTab } from '../components/alliance/SettingsTab';
import { RANK_LABELS } from '../components/alliance/shared';
import { type AllianceTab, allianceTabs, parseAllianceTab } from '../components/alliance/tabs';
import type { GameContext } from '../components/GameLayout';
import { ErrorNotice, Page, Spinner, type TabItem, TabPanel, Tabs } from '../components/ui';
import { LG_QUERY, useMediaQuery } from '../state/useMediaQuery';

const LABELS: Record<AllianceTab, string> = {
  overview: 'Overview',
  members: 'Members',
  attacks: 'Attacks',
  recruiting: 'Recruiting',
  settings: 'Settings',
};

export function AllianceScreen() {
  const { galaxyId, me } = useOutletContext<GameContext>();
  if (!me) return <Spinner />;
  return (
    <Page width="wide">
      {me.alliance ? (
        <MemberView galaxyId={galaxyId} allianceId={me.alliance.id} />
      ) : (
        <NoAllianceView galaxyId={galaxyId} rejoinAllowedAt={me.allianceRejoinAllowedAt} />
      )}
    </Page>
  );
}

function MemberView({ galaxyId, allianceId }: { galaxyId: string; allianceId: string }) {
  const alliance = useAlliance(galaxyId, allianceId);
  if (alliance.isPending) return <Spinner />;
  if (alliance.error) return <ErrorNotice error={alliance.error} />;
  if (alliance.data.view !== 'member') return <Spinner />;
  return <MemberTabs galaxyId={galaxyId} alliance={alliance.data} />;
}

function MemberTabs({ galaxyId, alliance }: { galaxyId: string; alliance: MemberAlliance }) {
  const [params, setParams] = useSearchParams();
  const desktop = useMediaQuery(LG_QUERY);
  const visible = allianceTabs(alliance.myRank, { desktop });
  const tab = parseAllianceTab(params.get('tab'), visible);
  const setTab = (next: AllianceTab) => setParams(next === 'overview' ? {} : { tab: next });
  const invites = useAllianceInvites(galaxyId, alliance.id, visible.includes('recruiting'));
  const applications = (invites.data?.items ?? []).filter((i) => i.kind === 'application').length;
  const attacks = useAllianceAttacks(galaxyId, alliance.id).data?.items.length ?? 0;
  const tabs: TabItem<AllianceTab>[] = visible.map((id) =>
    id === 'recruiting' && applications > 0
      ? {
          id,
          label: LABELS[id],
          badge: (
            <span
              data-applications-badge
              className="bg-accent px-1 font-mono text-[10px] text-accent-ink"
            >
              {applications}
            </span>
          ),
        }
      : id === 'attacks' && attacks > 0
        ? {
            id,
            label: LABELS[id],
            badge: (
              <span
                data-attacks-badge
                className="bg-accent px-1 font-mono text-[10px] text-accent-ink"
              >
                {attacks}
              </span>
            ),
          }
        : { id, label: LABELS[id] }
  );
  // From lg, the Overview tab shows the members beside it (Members has no tab there); on phones
  // only the selected one shows.
  const pair = tab === 'overview' || tab === 'members';
  const shown = (id: AllianceTab) => (tab === id ? '' : 'hidden lg:block');
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-3">
          <Emblem emblem={alliance.emblem} tag={alliance.tag} />
          <h1
            className="font-display text-xl font-bold uppercase tracking-[0.08em] text-text"
            data-alliance-name
          >
            {alliance.name} <span className="text-info">[{alliance.tag}]</span>
          </h1>
        </div>
        <p className="text-sm text-muted">
          {RANK_LABELS[alliance.myRank]} · {alliance.memberCount}/{alliance.maxMembers} members
        </p>
      </div>
      <Tabs label="Alliance" tabs={tabs} value={tab} onChange={setTab} />
      {pair ? (
        <div className="-mt-4 grid gap-4 lg:grid-cols-2 lg:items-start">
          <div className={shown('overview')}>
            <TabPanel id="overview">
              <OverviewTab galaxyId={galaxyId} alliance={alliance} />
            </TabPanel>
          </div>
          <div className={shown('members')}>
            <TabPanel id="members">
              <MembersTab galaxyId={galaxyId} alliance={alliance} />
            </TabPanel>
          </div>
        </div>
      ) : (
        <div className="-mt-4">
          <TabPanel id={tab}>
            {tab === 'attacks' ? (
              <AttacksTab galaxyId={galaxyId} alliance={alliance} />
            ) : tab === 'recruiting' ? (
              <RecruitingTab galaxyId={galaxyId} alliance={alliance} />
            ) : (
              <SettingsTab galaxyId={galaxyId} alliance={alliance} />
            )}
          </TabPanel>
        </div>
      )}
    </>
  );
}
